<template>
    <v-app style="background-color: #EFEFEF;">
        <modals></modals>
        <v-progress-circular :indeterminate="true" size="150" v-if="isLoading && !isSubmitLoading" class="fully-centered"></v-progress-circular>
        <div v-if="isSubmitLoading">
            <div class="confirmation-loading" v-if="!isError">
                <div class="loading-text" v-if="!isTimePassed">We are securely transmitting your information....</div>
                <div class="loading-text" v-if="isTimePassed">This is taking a little bit longer than normal, please be patient....</div>
                <div>
                    <v-progress-circular :indeterminate="true" size="150" color="#70ad47"></v-progress-circular>
                </div>
            </div>
            <div class="confirmation-loading" v-if="isError">
                <div class="loading-error-text">Uh Oh!</div>
                <div class="loading-error-text">We are really sorry.</div>
                <div class="loading-error-text">Something happened and we could not transmit your information. You can try again, or please tell the front desk staff</div>
                <div>
                    <v-icon color="red" style="font-size: 300px">warning</v-icon>
                </div>
            </div>
        </div>
        <main v-if="!isLoading && currentModel && !hasError" v-resize="onResize">
            <transition name="fade">
                <section id="headingSection" v-bind:class="{'has-ptt': showPtt && currentStep !== 0 && isWalkin, 'is-online': isOnline, 'in-lobby': currentStep === 0}">
                    <heading></heading>
                </section>
            </transition>
            <section id="innerSection"
                     v-bind:class="{'has-ptt': showPtt && currentStep !== 0 && isWalkin, 'in-lobby': currentStep === 0, 'is-online': isOnline, 'in-confirmation': this.$router.currentRoute.name === 'walkin_success' }">
                <router-view></router-view>
            </section>
            <transition name="fade">
                <section v-if="currentStep !== 0" id="actionsSection" v-bind:class="{'has-ptt': showPtt && isWalkin, 'is-online': isOnline}">
                    <actions></actions>
                </section>
            </transition>
        </main>
        <error v-if="hasError" type="error" :title="errorTitle" :content="errorContent"></error>
    </v-app>
</template>

<script>
    import Heading from './components/Heading'
    import Actions from './components/Actions'
    import Modals from './components/Modals'
    import Error from './components/Error'
    import ContextualMixin from './mixins/contextualMixin'

    export default {
        name: 'App',
        mixins: [ContextualMixin],
        data() {
            return {
                initialWindowDimensions: { height: 0, width: 0 },
                currentWindowDimensions: { height: 0, width: 0},
                initialOrientation: null,
                currentOrientation: null
            }
        },
        computed: {
            pageTitle() {
                return this.currentModel === null
                    ? 'Walk-in Express'
                    : `${this.currentModel.settings.clientName} | Walk-in Express`;
            },
            walkinNotSupported() { return (this.$route.name === 'walkin' || this.$route.name === 'walkin_legacy') && this.$store.state.walkinNotSupported; },
            clientNotFound() { return this.$store.state.clientNotFound; },
            clientError() { return this.$store.state.clientError; },
            serverError() { return this.$store.state.serverError; },
            hasError() {
                return this.walkinNotSupported || this.clientNotFound || this.clientError || !this.$route.params.clientId || this.serverError;
            },
            errorTitle() { 
                if (this.clientError || this.clientNotFound | !this.$route.params.clientId)
                    return 'Invalid Facility ID';
                if (this.serverError)
                    return 'Server Error';
                return 'Oops';
            },
            errorContent() {
                if (!this.hasError)
                    return '';
                if (this.clientError)
                    return this.clientError;
                if (this.clientNotFound)
                    return 'Facility not found';
                if (this.walkinNotSupported)
                    return this.currentModel.settings.localizedContent['walkin_noavailable_slots_message'];
                if (!this.$route.params.clientId)
                    return 'Facility ID is required';
                if (this.serverError)
                    return this.serverError;
                return 'Something went wrong';
            },
            orientationChanged() {
                return this.initialOrientation != this.currentOrientation;
            },
            inputFocusedMobile() {
                return (this.$vuetify.breakpoint.xs || this.isMobile()) && this.initialWindowDimensions.height > this.currentWindowDimensions.height && !this.orientationChanged;
            },
            buildRev() { return this.$store.state.buildRev; }
        },
        created() {
            let tis = this;
            this.initialWindowDimensions.width = window.innerWidth;
            this.initialWindowDimensions.height = window.innerHeight;
            this.initialOrientation = this.currentOrientation = screen.orientation.angle;
            window.addEventListener('orientationchange', () => {
                tis.currentOrientation = screen.orientation.angle;
            });
            window.addEventListener("beforeunload", function () {
                tis.$store.dispatch('unLock');
                return false;
            });
            window.addEventListener("unload", function () {
                tis.$store.dispatch('unLock');
                return false;
            });
        },
        mounted() {
            window.scrollTo(0, 1);
            if (!this.currentModel && this.clientId)
                this.$store.dispatch('fetchModel', this.clientId);
        },
        watch: {
            '$route.params.clientId': function (clientId) {
                //Logic to determine if:
                //client id is valid
                //we should reload the client model
                this.$store.state.clientNotFound = false;
                this.$store.state.walkinNotSupported = false;
                this.$store.state.clientError = null;
                if (clientId && this.isWalkin || this.currentStep === 0) {
                    if (isNaN(clientId)) {
                        this.$store.state.clientError = 'Facility ID must be a number';
                        return;
                    }
                    else if ((this.$store.state.currentModel && this.$store.state.currentModel.settings.clientId != clientId)
                        || this.invalidClient
                        || !this.$store.state.currentModel)
                        this.$store.dispatch('fetchModel', clientId);
                }
            },
            'currentModel.settings': function (m) {
                if (m.clientType === 'GROUP' && this.$router.currentRoute && !this.$router.currentRoute.name.includes('lobby')) {
                    this.backToLobby();
                }
            },
            'currentModel': function (m) {
                if (m && m.settings)
                    this.$store.state.invalidClient = m.settings.clientType !== 'GROUP' && (!m.availableTime || !m.availableTime.clientAvailableTimeId);
            }
        },
        methods: {
            onResize() {
                this.currentWindowDimensions.height = window.innerHeight;
                this.currentWindowDimensions.width = window.innerWidth;
            }
        },
        metaInfo() {
            return {
                title: this.pageTitle,
                meta: [
                    {
                        name:'version',
                        content: this.buildRev
                    }
                ]
            }
        },
        components: {
            Heading,
            Actions,
            Modals,
            Error
        }
    };
</script>
<style>

    html {
        overflow: hidden !important;
    }

    html,
    body,
    .v-application {
        height: 100%;
    }

    .container.fill-height > .row {
        max-width: 100% !important;
    }

    .fully-centered {
        position: fixed !important;
        top: 50%;
        left: 50%;
        -moz-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
    }

    .v-application--wrap {
        min-height: 100% !important;
    }

    #app button {
        text-transform: none !important;
    }

    main section, #headingSection > .container, #actionsSection > .container {
        width: 100%;
        padding: 0;
        overflow-x: hidden !important;
    }

    main {
        padding: 0;
        flex-direction: column;
        overflow: hidden;
        display: flex !important;
        min-height: 100%;
    }

    #innerSection .v-stepper,
    #innerSection .row,
    #innerSection .col, #innerSection .container {
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch;
    }

    #headingSection > .container > .row,
    #actionsSection > .container > .row {
        height: 100%;
        overflow: hidden;
    }

    #headingSection {
        top: 0;
        min-height: 80px;
        height: 13%;
    }

    #innerSection {
        flex: 1;
        flex-grow: 1;
        margin: 0 auto;
        max-width: 550px;
        overflow-y: auto !important;
    }

        #innerSection > .container {
            overflow: auto !important;
        }

    #actionsSection {
        height: 13%;
        max-height: 150px;
        min-height: 75px;
    }

    .container.fill-height > .row {
        max-width: unset !important;
        width: 100%;
    }

    #app .container,
    #app .v-stepper__header *,
    #app button,
    section,
    .v-responsive.v-image {
        transition: all 0.16s ease-in;
    }

    .v-card__title {
        word-break: break-word !important;
    }

    .v-application .v-card__title.headline {
        font-size: 18px !important;
        font-weight: 700 !important;
        justify-content: center !important;
        text-align: center !important;
        line-height: 20px !important;
        padding-top: 45px !important;
        padding-bottom: 20px !important;
    }

    .v-dialog > .v-card > .v-card__text {
        font-size: 18px !important;
        text-align: center !important;
        justify-content: center !important;
    }

    .v-card__actions {
        padding-bottom: 40px !important;
    }

    #glasbreakButtons,
    .v-card__actions {
        flex-direction: column !important;
    }

        #glasbreakButtons button,
        .v-card__actions button {
            font-size: 18px !important;
            font-weight: 700;
            width: 150px !important;
            height: 55px !important;
            margin: 0;
            margin-bottom: 15px;
        }

            #glasbreakButtons button:first-child {
                border: solid;
            }

    fieldset {
        transition: none !important;
    }

    .v-menu__content {
        max-height: 325px !important;
    }

    .v-messages__message {
        transition: none !important;
    }

    .fade-enter-active
    .fade-leave-active {
        transition: opacity 0s;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    @media (max-height: 600px) {
        #headingSection {
            height: 80px;
        }

        #actionsSection {
            height: 75px;
        }
    }

    .confirmation-loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        flex-direction: column;
    }

    .loading-text {
        margin-bottom: 5%;
        font-size: 25px;
        max-width: 70%;
        text-align: center;
        color: #6c6c6c;
    }

    .loading-error-text {
        margin-bottom: 1%;
        font-size: 25px;
        max-width: 70%;
        text-align: center;
        color: #6c6c6c;
    }
</style>